import React, { Suspense, lazy, useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { SellerProvider, useSeller } from './context/SellerContext';
import PrivateRoute from './components/PrivateRoute';
import ErrorBoundary from './components/common/ErrorBoundary';
import ScrollToTop from './components/ScrollToTop';
import Home from './pages/Home';
import ProductList from './pages/ProductList';
import Login from './pages/Login';
import Signup from './pages/Signup';
import { initKakao } from './utils/kakaoInit';
import { ToastProvider } from './context/ToastContext';
import { ConfirmProvider } from './context/ConfirmContext';
import { CategoryProvider } from './context/CategoryContext';
import './App.css';

import ShopLayout from './layouts/ShopLayout';

// Loading component
const PageLoading = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

// Public pages (lazy)
const ProductDetail = lazy(() => import('./pages/ProductDetail'));
const Cart = lazy(() => import('./pages/Cart'));
const OrderRegistration = lazy(() => import('./pages/OrderRegistration'));
const CheckoutSuccess = lazy(() => import('./pages/CheckoutSuccess'));
const CheckoutFail = lazy(() => import('./pages/CheckoutFail'));
const OAuthRedirect = lazy(() => import('./pages/auth/OAuthRedirect'));
const OAuthCallback = lazy(() => import('./pages/auth/OAuthCallback'));
const SubmallCallback = lazy(() => import('./pages/auth/SubmallCallback'));
const CompleteSignup = lazy(() => import('./pages/auth/CompleteSignup'));
const MyPage = lazy(() => import('./pages/MyPage'));
const OrderDetail = lazy(() => import('./pages/OrderDetail'));
const Inquiry = lazy(() => import('./pages/Inquiry'));
const GuestInquiry = lazy(() => import('./pages/GuestInquiry'));
// KakaoCallback / NaverCallback 은 OAuthCallback 으로 통합됨 (/auth/:provider/callback)
const ImpersonateHandler = lazy(() => import('./pages/auth/ImpersonateHandler'));
const AdminLogin = lazy(() => import('./pages/admin/AdminLogin'));
const PolicyPage = lazy(() => import('./pages/PolicyPage'));

// Admin pages (lazy)
const AdminLayout = lazy(() => import('./layouts/AdminLayout'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminProducts = lazy(() => import('./pages/admin/AdminProducts'));
const AdminProductForm = lazy(() => import('./pages/admin/AdminProductForm'));
const AdminCategories = lazy(() => import('./pages/admin/AdminCategories'));
const AdminOrders = lazy(() => import('./pages/admin/AdminOrders'));
const AdminPayments = lazy(() => import('./pages/admin/AdminPayments'));
const AdminAlimtalkHistory = lazy(() => import('./pages/admin/AdminAlimtalkHistory'));
const AdminAlimtalkCost = lazy(() => import('./pages/admin/AdminAlimtalkCost'));
const AdminCustomers = lazy(() => import('./pages/admin/AdminCustomers'));
const AdminInquiries = lazy(() => import('./pages/admin/AdminInquiries'));
const AdminSellers = lazy(() => import('./pages/admin/AdminSellers'));
const AdminSections = lazy(() => import('./pages/admin/AdminSections'));
const AdminBanners = lazy(() => import('./pages/admin/AdminBanners'));
const AdminStatistics = lazy(() => import('./pages/admin/AdminStatistics'));
const AdminSettings = lazy(() => import('./pages/admin/AdminSettings'));
const AdminSettlement = lazy(() => import('./pages/admin/AdminSettlement'));
const AdminNotices = lazy(() => import('./pages/admin/AdminNotices'));
const AdminReviews = lazy(() => import('./pages/admin/AdminReviews'));
const AdminSearch = lazy(() => import('./pages/admin/AdminSearch'));
const AdminProfile = lazy(() => import('./pages/admin/AdminProfile'));

// Seller pages (lazy)
const SellerLayout = lazy(() => import('./layouts/SellerLayout'));
const SellerDashboard = lazy(() => import('./pages/seller/SellerDashboard'));
const SellerProducts = lazy(() => import('./pages/seller/SellerProducts'));
const SellerProductForm = lazy(() => import('./pages/seller/SellerProductForm'));
const SellerOrders = lazy(() => import('./pages/seller/SellerOrders'));
const SellerCustomers = lazy(() => import('./pages/seller/SellerCustomers'));
const SellerSettlements = lazy(() => import('./pages/seller/SellerSettlements'));
const SellerSettings = lazy(() => import('./pages/seller/SellerSettings'));
const SellerBanners = lazy(() => import('./pages/seller/SellerBanners'));
const SellerProfile = lazy(() => import('./pages/seller/SellerProfile'));
const SellerStatistics = lazy(() => import('./pages/seller/SellerStatistics'));

function MainLayout() {
  const [cart, setCart] = useState([]);
  const { isSellerSite } = useSeller();

  useEffect(() => {
    initKakao();
  }, []);

  const addToCart = (product, quantity = 1, selectedOptions = []) => {
    const cartItemId = `${product.id}-${JSON.stringify(selectedOptions)}`;

    const existingItem = cart.find(item => item.cartItemId === cartItemId);

    if (existingItem) {
      setCart(cart.map(item =>
        item.cartItemId === cartItemId
          ? { ...item, quantity: item.quantity + quantity }
          : item
      ));
    } else {
      setCart([...cart, {
        ...product,
        cartItemId,
        quantity,
        selectedOptions,
        finalPrice: (product.memberPrice || product.price) + selectedOptions.reduce((sum, opt) => sum + opt.additionalPrice, 0)
      }]);
    }
  };

  const updateCartQuantity = (cartItemId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(cartItemId);
    } else {
      setCart(cart.map(item =>
        item.cartItemId === cartItemId ? { ...item, quantity } : item
      ));
    }
  };

  const removeFromCart = (cartItemId) => {
    setCart(cart.filter(item => item.cartItemId !== cartItemId));
  };

  const clearCart = () => {
    setCart([]);
  };

  return (
    <div className="App">
      <ErrorBoundary>
      <Suspense fallback={<PageLoading />}>
        <Routes>
          {/* Main Site Intro Page (Public Root) - No Layout */}
          <Route path="/" element={<Home cartCount={cart.reduce((sum, item) => sum + item.quantity, 0)} addToCart={addToCart} />} />

          {/* Shop Routes (Public) */}
          <Route element={<ShopLayout cartCount={cart.reduce((sum, item) => sum + item.quantity, 0)} />}>
            <Route path="/products" element={<ProductList addToCart={addToCart} />} />
            <Route path="/products/:id" element={<ProductDetail addToCart={addToCart} />} />
            <Route
              path="/cart"
              element={
                <Cart
                  cart={cart}
                  updateQuantity={updateCartQuantity}
                  removeItem={removeFromCart}
                />
              }
            />
            <Route path="/order/regist" element={<OrderRegistration cart={cart} clearCart={clearCart} />} />
            <Route path="/order/detail/:id" element={<OrderDetail />} />
            <Route path="/checkout/success" element={<CheckoutSuccess />} />
            <Route path="/checkout/fail" element={<CheckoutFail />} />

            {/* OAuth 가입정보 입력 (실제 입력 화면이므로 레이아웃 유지) */}
            <Route path="/auth/complete-signup" element={<CompleteSignup />} />

            {/* 사용자 페이지 (Private) */}
            <Route
              path="/mypage"
              element={
                <PrivateRoute>
                  <MyPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/orders/:id"
              element={
                <PrivateRoute>
                  <OrderDetail />
                </PrivateRoute>
              }
            />
            <Route path="/inquiry" element={<Inquiry />} />
            <Route path="/guest-inquiry" element={<GuestInquiry />} />
          </Route>

          {/* 공개 정책 페이지 (PG사 가맹점 심사용 공개 URL)
              ShopLayout 밖에 두고 PolicyPage가 isSellerSite에 따라 헤더/푸터를 자체 선택.
              본사 도메인 → IntroNav/IntroFooter (마켓플레이스 운영자 안내 형태)
              셀러 도메인 → SellerHeader/SellerFooter (기존 셀러 사이트 형태) */}
          <Route path="/policy/refund" element={<PolicyPage type="RETURN" title="취소·환불 정책" />} />
          <Route path="/terms" element={<PolicyPage type="TERMS" title="이용약관" />} />
          <Route path="/privacy" element={<PolicyPage type="PRIVACY" title="개인정보처리방침" />} />

          {/* 인증 '통과' 라우트 (레이아웃 없음)
              소셜 로그인은 메인 도메인 콜백을 거쳐 원래 몰로 되돌아오는데,
              이 화면들을 ShopLayout 안에 두면 처리되는 찰나에 헤더/카테고리 등 쇼핑몰 화면이
              통째로 렌더되어 '메인 페이지가 잠깐 보였다 사라지는' 깜빡임이 생긴다.
              화면 없이 처리만 하는 경유지이므로 레이아웃 밖에 둔다. */}
          <Route path="/auth/:provider" element={<OAuthRedirect />} />
          <Route path="/auth/:provider/callback" element={<OAuthCallback />} />
          <Route path="/auth/callback" element={<SubmallCallback />} />
          <Route path="/auth/impersonate" element={<ImpersonateHandler />} />

          {/* Standalone Pages (No Layout) */}
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/adminlogin" element={<AdminLogin />} />

          {/* Admin Routes */}
          <Route
            path="/admin"
            element={
              <PrivateRoute adminOnly={true}>
                <AdminLayout />
              </PrivateRoute>
            }
          >
            <Route index element={<AdminDashboard />} />
            <Route path="dashboard" element={<AdminDashboard />} />
            <Route path="profile" element={<AdminProfile />} />
            <Route path="search" element={<AdminSearch />} />
            <Route path="products" element={<AdminProducts />} />
            <Route path="products/new" element={<AdminProductForm />} />
            <Route path="products/edit/:id" element={<AdminProductForm />} />
            <Route path="categories" element={<AdminCategories />} />
            <Route path="orders" element={<AdminOrders />} />
            <Route path="payments" element={<AdminPayments />} />
            <Route path="alimtalk" element={<AdminAlimtalkHistory />} />
            <Route path="alimtalk/cost" element={<AdminAlimtalkCost />} />
            <Route path="customers" element={<AdminCustomers />} />
            <Route path="inquiries" element={<AdminInquiries />} />
            <Route path="sellers" element={<AdminSellers />} />
            <Route path="sections" element={<AdminSections />} />
            <Route path="banners" element={<AdminBanners />} />
            <Route path="statistics" element={<AdminStatistics />} />
            <Route path="settings" element={<AdminSettings />} />
            <Route path="settlement" element={<AdminSettlement />} />
            <Route path="notices" element={<AdminNotices />} />
            <Route path="reviews" element={<AdminReviews />} />
          </Route>

          {/* Seller Routes */}
          <Route
            path="/seller"
            element={
              <PrivateRoute sellerOnly={true}>
                <SellerLayout />
              </PrivateRoute>
            }
          >
            <Route index element={<SellerDashboard />} />
            <Route path="dashboard" element={<SellerDashboard />} />
            <Route path="products" element={<SellerProducts />} />
            <Route path="products/new" element={<SellerProductForm />} />
            <Route path="products/edit/:id" element={<SellerProductForm />} />
            <Route path="orders" element={<SellerOrders />} />
            <Route path="customers" element={<SellerCustomers />} />
            <Route path="settlements" element={<SellerSettlements />} />
            <Route path="banners" element={<SellerBanners />} />
            <Route path="settings" element={<SellerSettings />} />
            <Route path="statistics" element={<SellerStatistics />} />
            <Route path="profile" element={<SellerProfile />} />
          </Route>
        </Routes>
      </Suspense>
      </ErrorBoundary>
    </div>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <SellerProvider>
          <CategoryProvider>
            <ToastProvider>
              <ConfirmProvider>
                <ScrollToTop />
                <MainLayout />
              </ConfirmProvider>
            </ToastProvider>
          </CategoryProvider>
        </SellerProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
